// Gerador de PDF de relatório individual de emissão por CTe (PO1-1996).
// Client-side via jsPDF, sem round-trip de geração no backend.
//
// Aceita CTe ainda não calculado: imprime "pendente de cálculo" e
// detalha o motivo (FonteDistancia ou 'nao_processado').
// jsPDF v2 usa named export. v3+ trazia dep transitiva (fast-png) com sintaxe
// que o babel/CRA atual não compila — por isso fixamos em ^2.5.2.
import { jsPDF } from "jspdf";
import { formatDateLabel } from "../../../../utils/dateOnly";
import { rotuloDocumento } from "../../../../utils/modeloDocumento";

export const COR_PRIMARIA = [29, 140, 248];
export const COR_TEXTO = [40, 40, 60];
export const COR_LABEL = [110, 110, 130];

export const fmtData = (iso) => {
  return formatDateLabel(iso, "-");
};

export const fmtNumero = (n, frac = 2) => {
  if (n == null || Number.isNaN(Number(n))) return "-";
  return Number(n).toLocaleString("pt-BR", {
    minimumFractionDigits: frac,
    maximumFractionDigits: frac,
  });
};

export const motivoLabel = (m) => {
  const map = {
    pendente_provedor: "Aguardando provedor de distância (ORS)",
    sem_origem_destino: "CTe sem CEP/cidade de origem ou destino",
    sem_peso: "CTe sem peso (KG/TON) cadastrado",
    sem_fator_modal: "Modal sem fator de emissão cadastrado",
    nao_processado: "Cálculo ainda não executado para este CTe",
    erro: "Falha técnica no cálculo",
  };
  return map[m] || m || "-";
};

export function escreverLinha(doc, label, valor, x, y) {
  doc.setTextColor(...COR_LABEL);
  doc.setFontSize(9);
  doc.text(label.toUpperCase(), x, y);
  doc.setTextColor(...COR_TEXTO);
  doc.setFontSize(11);
  doc.text(String(valor || "-"), x, y + 5);
}

export function gerarRelatorioEmissaoPdf(detalhe) {
  if (!detalhe) {
    throw new Error("Detalhe do CTe ausente para gerar relatório.");
  }

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();

  // Cabeçalho com banda primária
  doc.setFillColor(...COR_PRIMARIA);
  doc.rect(0, 0, pageW, 28, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.text("Relatório de Emissão de CO₂", 15, 14);
  doc.setFontSize(11);
  doc.text("Everlog · Indicador de Sustentabilidade", 15, 22);

  // Bloco principal
  let y = 42;
  doc.setTextColor(...COR_TEXTO);
  doc.setFontSize(13);
  doc.setFont(undefined, "bold");
  doc.text(
    `${rotuloDocumento(detalhe.modelo)} ${detalhe.numeroCte || detalhe.idCte}`,
    15,
    y
  );
  doc.setFont(undefined, "normal");
  doc.setFontSize(10);
  doc.setTextColor(...COR_LABEL);
  if (detalhe.chaveAcesso) {
    doc.text(`Chave: ${detalhe.chaveAcesso}`, 15, y + 6);
  }

  y += 16;

  // Dados do CTe (3 colunas)
  escreverLinha(doc, "Embarcador", detalhe.embarcador, 15, y);
  escreverLinha(doc, "Transportadora", detalhe.transportadora, 80, y);
  escreverLinha(doc, "Data de emissão", fmtData(detalhe.dataEmissao), 145, y);

  y += 14;

  escreverLinha(
    doc,
    "Origem",
    [detalhe.ufOrigem, detalhe.cidadeOrigem].filter(Boolean).join(" - ") || "-",
    15,
    y
  );
  escreverLinha(
    doc,
    "Destino",
    [detalhe.ufDestino, detalhe.cidadeDestino].filter(Boolean).join(" - ") || "-",
    80,
    y
  );
  escreverLinha(doc, "Modal", detalhe.modal || "-", 145, y);

  y += 18;

  // Bloco emissão (com box)
  doc.setDrawColor(220, 220, 230);
  doc.setLineWidth(0.3);
  doc.roundedRect(15, y, pageW - 30, 60, 2, 2, "S");

  doc.setTextColor(...COR_PRIMARIA);
  doc.setFontSize(11);
  doc.setFont(undefined, "bold");
  doc.text("Cálculo de emissão", 20, y + 8);
  doc.setFont(undefined, "normal");

  if (detalhe.calculado && detalhe.emissao) {
    const e = detalhe.emissao;
    escreverLinha(doc, "Distância (km)", fmtNumero(e.distanciaKm), 20, y + 18);
    escreverLinha(doc, "Peso (ton)", fmtNumero(e.pesoToneladas, 3), 70, y + 18);
    escreverLinha(
      doc,
      "Fator (kg CO₂/ton·km)",
      fmtNumero(e.fatorUsado, 3),
      120,
      y + 18
    );

    escreverLinha(
      doc,
      "Emissão calculada",
      `${fmtNumero(e.emissaoKgCO2)} kg CO₂`,
      20,
      y + 36
    );
    escreverLinha(
      doc,
      "Fonte da distância",
      e.fonteDistancia || "-",
      80,
      y + 36
    );
    escreverLinha(
      doc,
      "Calculado em",
      fmtData(e.calculadoEm),
      140,
      y + 36
    );

    // Fórmula em rodapé do bloco — quebrada em 2 linhas pra caber bem.
    doc.setTextColor(...COR_LABEL);
    doc.setFontSize(9);
    const formulaLinha1 = "Fórmula: distância (km) × peso (ton) × fator";
    const formulaLinha2 = `${fmtNumero(e.distanciaKm)} × ${fmtNumero(
      e.pesoToneladas,
      3
    )} × ${fmtNumero(e.fatorUsado, 3)} = ${fmtNumero(e.emissaoKgCO2)} kg CO₂`;
    doc.text(formulaLinha1, 20, y + 50);
    doc.text(formulaLinha2, 20, y + 56);
  } else {
    doc.setTextColor(245, 158, 11);
    doc.setFontSize(11);
    doc.setFont(undefined, "bold");
    doc.text("Pendente de cálculo", 20, y + 22);
    doc.setFont(undefined, "normal");
    doc.setTextColor(...COR_TEXTO);
    doc.setFontSize(10);
    doc.text(`Motivo: ${motivoLabel(detalhe.pendencia)}`, 20, y + 32);
    if (detalhe.cancelado) {
      doc.setTextColor(220, 60, 60);
      doc.text("Observação: CTe está cancelado/inativo.", 20, y + 42);
    }
  }

  y += 70;

  // Rodapé
  doc.setTextColor(...COR_LABEL);
  doc.setFontSize(8);
  const geradoEm = new Date().toLocaleString("pt-BR");
  doc.text(
    `Documento gerado em ${geradoEm} · Everlog Brasil`,
    15,
    doc.internal.pageSize.getHeight() - 10
  );

  // Download direto
  const filename = `emissao-cte-${detalhe.numeroCte || detalhe.idCte}.pdf`;
  doc.save(filename);
}

export default gerarRelatorioEmissaoPdf;
